import { Star, StarBorder } from '@mui/icons-material';
import { Grid, Tooltip, Typography } from '@mui/material';
import { HSpacer, RoutalAvatar, RoutalDialog, RoutalPalette } from '@nx-smartmonkey/ui';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import * as routing from '../../../constants/Routing';
import { PLAN_TYPE } from '../../../constants/Subscriptions';
import ConfirmDialog from '../../../dialogs/ConfirmDialog';
import { FlatProject } from '../../../domain/project/FlatProject';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useRetrieveOrganizationResources } from '../../../hooks/organization/useRetrieveOrganizationResources';
import { useUpdateOrganization } from '../../../hooks/organization/useUpdateOrganization';
import { useRetrieveFlatProjects } from '../../../hooks/project/useRetrieveFlatProjects';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import NoProjects from '../../../pages/main/NoProjects';
import Button from '../../../react-components/Button';
import FormattedMessage from '../../../react-components/FormattedCustomMessage';
import Text from '../../../react-components/Text';
import { DataTable } from '../../../react-components/data-table/DataTable';
import { getUploadAsBase64 } from '../../../services/requests';
import { OrganizationNewProjectDialog } from './new_project_dialog';

export const OrganizationProjects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: organization } = useRetrieveOrganization();
  const { data: organizationResources } = useRetrieveOrganizationResources();
  const { data: user } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: flatProjects } = useRetrieveFlatProjects({ user });
  const { run: organizationEdit } = useUpdateOrganization();
  const [hoverElementProjectId, setHoverElementProjectId] = useState(``);
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    if (location.search) {
      const searchParams = queryString.parse(location.search);
      if (searchParams.create && searchParams.create === `true`) {
        setNewProjectDialogOpen(true);
      }
    }
  }, [setNewProjectDialogOpen, location]);

  if (!flatProjects) return null;

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{
        padding: `16px 22px`,
        backgroundColor: `white`,
        zIndex: 2,
        height: `100%`,
        overflowY: `auto`,
      }}
    >
      <Grid container direction="column">
        <Grid container direction="column" style={{ padding: `16px 22px` }}>
          <Grid item container direction="row" justifyContent="space-between" alignItems="center" wrap="nowrap">
            <Grid item>
              <Text variant="h6">
                <FormattedMessage
                  id="settings.organization.projects.title"
                  values={{ organizationName: organization?.label }}
                />
              </Text>
            </Grid>

            <Grid item>
              <Grid container>
                {userCredentials?.planner.test(`can_manage_projects`) && (
                  <Grid item>
                    <Button
                      styleType="primary"
                      variant="default"
                      onClick={() => {
                        navigate(`${routing.SETTINGS.ORGANIZATION.PROJECTS}?create=true`);
                      }}
                      intlId="settings.organization.projects.buttons.create"
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        direction="column"
        style={{
          padding: `16px 22px`,
        }}
        wrap="nowrap"
      >
        {flatProjects.length === 0 && <NoProjects />}

        {flatProjects.length > 0 ? (
          <DataTable
            noFooter
            docs={flatProjects.map((project) => ({
              doc: project,
            }))}
            showCheckboxes={false}
            total={flatProjects.length}
            onBodyMouseLeave={() => setHoverElementProjectId(``)}
            columns={[
              {
                width: 500,
                accessorName: `name`,
                name: (
                  <Text
                    style={{ color: RoutalPalette.primary20 }}
                    isBold
                    intlId="settings.organization.projects.name"
                  />
                ),
                valueCellStyle: {
                  width: `100%`,
                },
                formatter: ({ doc: project, rowHover }) => {
                  if (rowHover) {
                    setHoverElementProjectId(project.id);
                  }

                  return (
                    <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
                      <Grid item>
                        <RoutalAvatar
                          url={project.avatar_url}
                          getUploadAsBase64={getUploadAsBase64}
                          label={project.label}
                          labelStyles={{ fontSize: `12px`, lineHeight: 2, cursor: `default` }}
                          containerStyles={{ width: `32px`, height: `32px` }}
                        />
                      </Grid>

                      <HSpacer medium />

                      <Grid container direction="column" justifyContent="center">
                        <Grid item>
                          <Grid container direction="column">
                            <Grid item container direction="row" alignItems="center">
                              <Tooltip placement="top" title={project.label ?? ``}>
                                <Text variant="body-large">
                                  {(project.label ?? ``).length > 30
                                    ? `${(project.label ?? ``).substring(0, 30)}...`
                                    : `${project.label}`}
                                </Text>
                              </Tooltip>

                              {project.id === organization?.default_project_id && (
                                <Tooltip
                                  title={
                                    <span style={{ textAlign: `center` }}>
                                      <FormattedMessage id="settings.organization.projects.default_project" />
                                    </span>
                                  }
                                >
                                  <Star style={{ color: RoutalPalette.warning, marginTop: `-4px` }} />
                                </Tooltip>
                              )}

                              {userCredentials?.planner.test(`can_manage_projects`) &&
                              project.id !== organization?.default_project_id &&
                              hoverElementProjectId === project.id ? (
                                <Tooltip
                                  title={
                                    <span style={{ textAlign: `center` }}>
                                      <FormattedMessage id="settings.organization.projects.set_default_project" />
                                    </span>
                                  }
                                >
                                  <StarBorder
                                    style={{ color: RoutalPalette.warning, cursor: `pointer`, marginTop: `-4px` }}
                                    onClick={() => {
                                      organizationEdit({
                                        defaultProjectId: project.id,
                                        organization: organization!,
                                      });
                                    }}
                                  />
                                </Tooltip>
                              ) : null}
                            </Grid>

                            {project.description ? (
                              <Grid item container direction="row">
                                <Typography
                                  variant="h5"
                                  style={{
                                    fontSize: `16px`,
                                    color: `#90949c`,
                                    height: `18px`,
                                    maxWidth: `320px`,
                                    overflow: `hidden`,
                                    textOverflow: `ellipsis`,
                                    whiteSpace: `nowrap`,
                                  }}
                                >
                                  {project.description}
                                </Typography>
                              </Grid>
                            ) : null}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                },
              },
              {
                width: 80,
                accessorName: `users`,
                name: (
                  <Text
                    style={{ color: RoutalPalette.primary20, textTransform: `none` }}
                    isBold
                    intlId="settings.organization.projects.new_users"
                  />
                ),
                align: `center`,
                formatter: ({ doc: flatProject }: { doc: FlatProject }) => (
                  <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
                    <Grid item>
                      <Text variant="body">{flatProject.users ?? 0}</Text>
                    </Grid>
                  </Grid>
                ),
              },
              {
                width: 80,
                accessorName: `vehicles`,
                name: (
                  <Text
                    style={{ color: RoutalPalette.primary20 }}
                    isBold
                    intlId="settings.organization.projects.vehicles"
                  />
                ),
                align: `center`,
                formatter: ({ doc: flatProject }: { doc: FlatProject }) => {
                  const projectResources = organizationResources?.getProjectResources(flatProject.id);
                  return (
                    <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
                      <Grid item>
                        <Text variant="body">{projectResources?.max_vehicles_plan ?? 0}</Text>
                      </Grid>
                    </Grid>
                  );
                },
              },
              {
                width: 80,
                accessorName: `clients`,
                name: (
                  <Text
                    style={{ color: RoutalPalette.primary20 }}
                    isBold
                    intlId="settings.organization.projects.clients"
                  />
                ),
                align: `center`,
                formatter: ({ doc: flatProject }: { doc: FlatProject }) => {
                  const projectResources = organizationResources?.getProjectResources(flatProject.id);
                  return (
                    <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
                      <Grid item>
                        <Text variant="body">{projectResources?.max_clients ?? 0}</Text>
                      </Grid>
                    </Grid>
                  );
                },
              },
              {
                width: 80,
                accessorName: `services_per_plan`,
                name: (
                  <Text
                    style={{ color: RoutalPalette.primary20, textTransform: `none` }}
                    isBold
                    intlId="settings.organization.projects.services_per_plan"
                  />
                ),
                align: `center`,
                formatter: ({ doc: flatProject }: { doc: FlatProject }) => {
                  const projectResources = organizationResources?.getProjectResources(flatProject.id);
                  return (
                    <Grid container direction="row" wrap="nowrap" justifyContent="center" alignItems="center">
                      <Grid item>
                        <Text variant="body">{projectResources?.max_stops_plan ?? 0}</Text>
                      </Grid>
                    </Grid>
                  );
                },
              },
              {
                width: 150,
                accessorName: `actions`,
                align: `right`,
                formatter: ({ doc: flatProject }: { doc: FlatProject }) => (
                  <Button
                    styleType="primary"
                    variant="default"
                    onClick={() => {
                      navigate(`${routing.PROJECT_SETTINGS.GENERAL.replace(`:projectId`, `${flatProject?.id}`)}`);
                    }}
                  >
                    {userCredentials?.planner.test(`can_manage_projects`) ? (
                      <Text
                        variant="body"
                        style={{ color: `white`, cursor: `pointer` }}
                        intlId="settings.organization.projects.buttons.settings"
                      />
                    ) : (
                      <Text
                        variant="body"
                        style={{ color: `white`, cursor: `pointer` }}
                        intlId="settings.organization.projects.buttons.view"
                      />
                    )}
                  </Button>
                ),
              },
            ]}
          />
        ) : null}
      </Grid>

      {organization?.subscription.plan !== PLAN_TYPE.MONKEY ? (
        <>
          {userCredentials?.planner.test(`can_manage_projects`) ? (
            <OrganizationNewProjectDialog
              isOpen={newProjectDialogOpen}
              onClose={() => {
                setNewProjectDialogOpen(false);
                navigate(routing.SETTINGS.ORGANIZATION.PROJECTS);
              }}
            />
          ) : (
            <RoutalDialog
              open={newProjectDialogOpen}
              onCloseDialog={() => setNewProjectDialogOpen(false)}
              titleContent={<Text variant="h6" intlId="organization.not_privileged.title" />}
              footerContent={
                <Grid container justifyContent="flex-end">
                  <Button
                    intlId="organization.not_privileged.closedialog"
                    variant="flat"
                    onClick={() => setNewProjectDialogOpen(false)}
                  />
                </Grid>
              }
            >
              <Text variant="body-medium" intlId="organization.not_privileged.subtitle" />
            </RoutalDialog>
          )}
        </>
      ) : (
        <ConfirmDialog
          open={newProjectDialogOpen}
          onCloseDialog={() => setNewProjectDialogOpen(false)}
          title={<Text variant="h6" intlId="subscription.not_privileged.title" />}
          description={<Text intlId="subscription.not_privileged.subtitle" />}
          cancelIntlId="subscription.not_privileged.closedialog"
          confirmIntlId="subscription.not_privileged.openplans"
          onConfirm={() => {
            navigate(routing.SETTINGS.BILLING.SUBSCRIPTION);
          }}
        />
      )}
    </Grid>
  );
};
